/* One turn of the Copilot conversation — the question, then its answer — shared by the drawer and
 * the `/copilot` page.
 *
 * **One component for both doors**, because the rules an answer is presented by are the module's
 * whole safety story and a second rendering is how two places come to disagree about them:
 * `provenanceOf` says who wrote the words (only `answered` carries model prose), `refusalSummary`
 * names the kinds the asker could not read, and a status is a word as well as a colour.
 *
 * **A citation names its record.** A dataset reads as its catalogue name, resolved through the
 * catalogue's own lookup; any other kind reads as its kind, with the reference on hover. A chip
 * carrying a bare id would be the raw-identifier defect this product has shipped and fixed before.
 * It navigates for the kinds whose reference is a record this product has a page for; any other
 * kind stays a plain chip, because a link that resolved to a 404 would be worse than one that does
 * not pretend to go anywhere.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { useDatasetNames } from '@/features/catalog-browser';
import { SCREEN_PATHS } from '@/shared/routes';
import { CopilotMascot } from '@/shared/ui/CopilotMascot';
import type { MascotState } from '@/shared/ui/CopilotMascot';

import {
  STATUS_LABEL,
  isModelAuthored,
  presentationFor,
  provenanceOf,
  receiptsFrom,
  refusalSummary,
  segmentsFrom,
} from '../model/copilot';
import type { Receipt } from '../model/copilot';
import type { ShownAnswer, Turn } from '../model/thread';

/** Where a citation lands, for the kinds whose reference is a record id with a page of its own. */
function routeFor(receipt: Receipt): string | null {
  if (receipt.ref === '') return null;
  switch (receipt.kind) {
    case 'dataset':
      return generatePath(SCREEN_PATHS.dataset, { datasetId: receipt.ref });
    case 'incident':
      return generatePath(SCREEN_PATHS.incident, { incidentId: receipt.ref });
    case 'rule':
      return generatePath(SCREEN_PATHS.rule, { ruleId: receipt.ref });
    case 'score':
      return SCREEN_PATHS.scores;
    default:
      return null;
  }
}

/** The kinds a citation can name, as a reader says them. Anything else is shown as it came. */
const KIND_KEY: Readonly<Record<string, string>> = {
  dataset: 'copilot.cite.dataset',
  rule: 'copilot.cite.rule',
  score: 'copilot.cite.score',
  incident: 'copilot.cite.incident',
  lineage: 'copilot.cite.lineage',
};

function Citations({
  receipts,
  onNavigate,
}: {
  readonly receipts: readonly Receipt[];
  readonly onNavigate: (to: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const names = useDatasetNames();

  return (
    <ul className="lq-cop__cites" aria-label={t('copilotDrawer.citations')}>
      {receipts.map((receipt) => {
        const to = routeFor(receipt);
        const kindKey = KIND_KEY[receipt.kind];
        const kind =
          kindKey === undefined
            ? receipt.kind === ''
              ? t('copilot.receiptUnkinded')
              : receipt.kind
            : t(kindKey);
        const name =
          receipt.kind === 'dataset' ? names.data?.byId.get(receipt.ref) : undefined;
        const content = (
          <>
            <span className="lq-cop__citeNum">{receipt.n}</span>
            <span className="lq-cop__citeKind">{kind}</span>
            {name === undefined ? null : <span className="lq-cop__citeName">{name}</span>}
          </>
        );
        return (
          <li key={`${receipt.kind}:${receipt.ref}`}>
            {to === null ? (
              <span className="lq-cop__cite" title={receipt.ref}>
                {content}
              </span>
            ) : (
              <button
                type="button"
                className="lq-cop__cite lq-cop__cite--link"
                title={receipt.ref}
                onClick={() => {
                  onNavigate(to);
                }}
              >
                {content}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function AnswerCard({
  answer,
  onNavigate,
}: {
  readonly answer: ShownAnswer;
  readonly onNavigate: (to: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [contextOpen, setContextOpen] = useState(false);
  const names = useDatasetNames();
  const receipts = receiptsFrom(answer.citations);
  const authored = isModelAuthored(answer.status);
  const segments = authored
    ? segmentsFrom(answer.text, receipts)
    : [{ text: answer.text, marker: null }];
  const rejection = answer.rejection_reason ?? '';
  const context = answer.context_preview ?? '';

  return (
    <article className="lq-cop__answer" data-status={answer.status}>
      <div className="lq-cop__answerHead">
        <span className="lq-cop__status" data-status={answer.status}>
          <span className="lq-cop__statusDot" aria-hidden="true" />
          {STATUS_LABEL[answer.status]}
        </span>
        <span className="lq-cop__provenance">
          {provenanceOf(answer.status, answer.validator)}
        </span>
      </div>
      <p className="lq-cop__text">
        {segments.map((segment, index) => {
          /* The token a marker replaced named a record. Where it is a dataset with a catalogue
             name, the name stays in the sentence — "because retail.transactions¹ lost…" rather
             than "because ¹ lost…" — so the prose still reads; any other kind keeps the marker
             alone, and its chip below says what it is. */
          const cited = receipts.find((receipt) => receipt.n === segment.marker);
          const name =
            cited?.kind === 'dataset' ? names.data?.byId.get(cited.ref) : undefined;
          return (
            <span key={String(index)}>
              {segment.text}
              {name === undefined ? null : (
                <span className="lq-cop__inlineName">{name}</span>
              )}
              {segment.marker === null ? null : (
                <sup className="lq-cop__marker">{segment.marker}</sup>
              )}
            </span>
          );
        })}
      </p>
      {answer.status === 'refused' && rejection !== '' ? (
        <p className="lq-cop__note lq-cop__note--warn">
          {t('copilot.rejectionReason', { reason: rejection })}
        </p>
      ) : null}
      {/* A fault is ours — escalated, and never dressed as an outage or a model's refusal. */}
      {presentationFor(answer.status) === 'fault' ? (
        <p className="lq-cop__note lq-cop__note--error">{t('copilot.faultHint')}</p>
      ) : null}
      {receipts.length > 0 ? (
        <Citations receipts={receipts} onNavigate={onNavigate} />
      ) : null}
      {answer.truncated ? (
        <p className="lq-cop__note lq-cop__note--warn">{t('copilotDrawer.truncated')}</p>
      ) : null}
      {/* **A deliberate permissions disclosure**: an answer thin because of permissions and one thin
          because nothing matched are different facts, and only this line separates them. */}
      {answer.refused_kinds.length > 0 ? (
        <p className="lq-cop__note">
          {t('copilotDrawer.refused', { kinds: refusalSummary(answer.refused_kinds) })}
        </p>
      ) : null}
      {context === '' ? null : (
        <div className="lq-cop__context">
          <button
            type="button"
            className="lq-link-button"
            aria-expanded={contextOpen}
            onClick={() => {
              setContextOpen(!contextOpen);
            }}
          >
            {contextOpen
              ? t('copilotDrawer.contextHide')
              : t('copilotDrawer.contextShow', { count: answer.snippet_count ?? 0 })}
          </button>
          {contextOpen ? <pre className="lq-cop__preview">{context}</pre> : null}
        </div>
      )}
    </article>
  );
}

function mascotFor(turn: Turn): MascotState {
  if (turn.state === 'pending') return 'thinking';
  if (turn.state === 'failed') return 'error';
  return turn.answer.status === 'answered'
    ? 'success'
    : turn.answer.status === 'platform_error'
      ? 'error'
      : 'idle';
}

/** A question and its answer. `withMascot` is the page's layout, where the answer sits beside the
 * Copilot's mark; the drawer's header already carries one. */
export function TurnView({
  turn,
  onNavigate,
  withMascot = false,
}: {
  readonly turn: Turn;
  readonly onNavigate: (to: string) => void;
  readonly withMascot?: boolean;
}): ReactElement {
  const { t } = useTranslation();

  const reply =
    turn.state === 'pending' ? (
      <div className="lq-cop__pending" role="status">
        {withMascot ? null : <CopilotMascot state="thinking" size="sm" />}
        {t('copilotDrawer.thinking')}
      </div>
    ) : turn.state === 'failed' ? (
      <article className="lq-cop__answer" data-status="platform_error">
        <div className="lq-cop__answerHead">
          <span className="lq-cop__status" data-status="platform_error">
            <span className="lq-cop__statusDot" aria-hidden="true" />
            {t('copilotDrawer.failed')}
          </span>
        </div>
        <p className="lq-cop__text">{turn.message}</p>
      </article>
    ) : (
      <AnswerCard answer={turn.answer} onNavigate={onNavigate} />
    );

  return (
    <li className="lq-cop__turn">
      <p className="lq-cop__question">{turn.question}</p>
      {withMascot ? (
        <div className="lq-cop__reply">
          <CopilotMascot state={mascotFor(turn)} size="sm" />
          <div className="lq-cop__replyBody">{reply}</div>
        </div>
      ) : (
        reply
      )}
    </li>
  );
}
